<script lang="ts" setup>
import type { Statement } from '@/types/domain'
import type {StatementEvaluationResult } from '@/types/domain'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n';
import { ColorScaleKey } from '@/ui/colorScaleContext'
import { ModuleLocaleKey } from '@/i18n/moduleLocaleContext';
import { DomainTransKey } from '@/i18n/domainTrans';

const {t} = useI18n();
const dt = inject(DomainTransKey)!
const moduleLocale = inject(ModuleLocaleKey, { baseLocale: computed(() => 'no') })

const emit = defineEmits<{
  (e: 'save', payload: StatementEvaluationResult): void
  (e: 'save-and-next', payload: StatementEvaluationResult): void
  (e: 'skip'): void
  (e: 'close'): void
}>()

const props = defineProps<{
  hasNext: boolean
  statement: Statement
}>()

const _id = ref(props.statement.id);
const _eval = ref(props.statement.evaluation ?? 50);

const colorCtx = inject(ColorScaleKey)!
const $color = computed(() => colorCtx.scale.value.at(_eval.value))

watch(() => props.statement, (newStatement) => {
  _id.value = newStatement.id;
  _eval.value = newStatement.evaluation ?? 50;
}, { immediate: true });




const $text = computed(() => {
  if (_eval.value == null) return t('likert.unanswered');

  const index = Math.round(_eval.value / 20);
  return t(`likert.${index}`);
});

const onSave = (() => {
  if(_eval.value == null) {
    emit('close')
    return
  }

  emit('save', {
    statementId: _id.value,
    evaluation: _eval.value
  })
})

const onSaveAndNext = (() => {
  if(_eval.value == null) {
    emit('skip')
    return
  }

  emit('save-and-next', {
    statementId: _id.value,
    evaluation: _eval.value
  })
})
</script>

<template>
  <div class="modal-container" @click.self="emit('close')">
    <div class="modal-body">
      <span class="modal-close" @click="emit('close')">X</span>
      <h3 class="header">{{ dt.t(statement.title, statement.title_i18n, moduleLocale.baseLocale.value) }}</h3>
      <h3 :style="{'color': $color}">{{ $text }}</h3>
      <span>({{ _eval }} / 100)</span>
      <div class="slider" >
        <vue3-slider v-model="_eval" :color="$color" track-color="#FEFEFE"
              :alwaysShowHandle=true :handleScale=3 orientation="horizontal"
              width="60vw" :circleGap=270 :height=12 @keyup.enter="onSaveAndNext()" @keyup.esc="emit('close')"/>
      </div>
      <div class="modal-action">
        <button class="modal-button cancel" @click="emit('close')">{{ $t('common.close') }}</button>
        <button class="modal-button confirm" @click="onSave()">{{ $t('common.OK') }}</button>
        <button class="modal-button continue" v-if="hasNext" @click="onSaveAndNext()">{{ $t('common.OK-next') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.flex-wrapper {
  display: flex;
  flex-direction: column;
}

.header {
  padding-bottom: 40px;
}

.modal-container {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100vh;
    background-color: #2c3e50b4;
  }

.modal-body {
    color: #f0f0f0;
    background-color: #2c3e50;
    border-top: 2px solid #003a74;
    border-bottom: 2px solid #003a74;

    text-align: center;
    padding: 20px 40px;
    display: flex;
    flex-direction: column;
    width: 100%;
}

.slider {
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 40px 0;
  touch-action: none;
}
.modal-action {
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-evenly;
}

button.modal-button {
  touch-action: manipulation;
  cursor: pointer;
  min-height: 45px;
}

.modal-close {
  cursor: pointer;
  position: relative;
  align-self: end;
  right: -33px;
  top: -17px;
}
</style>
